/* @flow */
/**
 * External dependencies
 */
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { TransitionGroup } from 'react-transition-group';
import Loadable from 'react-loadable';
import React, { Component } from 'react';
import { noop } from 'lodash';

/**
 * Internal dependencies
 */
import { api } from 'shared/config';
import { canUseDOM } from './utils/canUseDom';
import { getUrlFromState } from 'shared/api';
import { incrementProgress } from './actions/user';
import LoadingScreen from './components/loading';
import Transition from './components/transition';
import Video from './views/video';
import IntroVideo from './views/video/introVideo';
import withApiData from './components/higher-order/with-api-data';

/**
 * Style
 */
import './style/global';
import { Root } from './style';

/**
 * Flow
 */
import type { Dispatch } from 'redux';
import type { Theme } from 'styled-components';
import type { UserState } from './reducers/user';

type RouterProps = UserState & {
	dispatch: Dispatch<any>,
	isLoading: boolean,
	data: Object,
	theme: Theme,
	urlKey: string,
	reset: () => void,
};

type RouterState = {
	done: boolean,
	endVideo: null | string,
	endVideoDone: Function,
};

export type ScreenProps = {
	nextTask: () => void,
	dispatch: Dispatch<*>,
	root: any,
};

/**
 * Components
 */
const Gender = Loadable( {
	loader: () => import( './views/gender'/* webpackChunkName: "gender" */ ),
	loading: LoadingScreen,
} );

const Pin = Loadable( {
	loader: () => import( './views/pin'/* webpackChunkName: "pin" */ ),
	loading: LoadingScreen,
} );

const Chat = Loadable( {
	loader: () => import( './views/chat'/* webpackChunkName: "chat" */ ),
	loading: LoadingScreen,
} );

const Inboxes = Loadable( {
	loader: () => import( './views/inboxes'/* webpackChunkName: "inboxes" */ ),
	loading: LoadingScreen,
} );

const Questions = Loadable( {
	loader: () => import( './views/questions'/* webpackChunkName: "questions" */ ),
	loading: LoadingScreen,
} );

const Credits = Loadable( {
	loader: () => import( './views/credits'/* webpackChunkName: "credits" */ ),
	loading: LoadingScreen,
} );

const ConfirmSound = Loadable( {
	loader: () => import( './views/confirm-sound'/* webpackChunkName: "confirm-sound" */ ),
	loading: LoadingScreen,
} );

let hasSaved = false;

class Routes extends Component<RouterProps, RouterState> {
	root = React.createRef();
	state = {
		done: false,
		endVideo: null,
		endVideoDone: noop,
	};

	componentDidMount() {
		window.nextTask = this.nextTask;
	}

	setEndVideo = ( url, fn ) => {
		this.setState( { endVideo: url, endVideoDone: fn } );
	}

	getTask( offset: number = 0, key = this.getKey() ) {
		const { data } = this.props;

		let progress = this.props.progress[ key ];
		progress += offset;

		if ( data[ key ] ) {
			return data[ key ][ progress ];
		}
	}

	getKey() {
		const { data } = this.props;

		if ( ( this.props.progress.init === 'done' || ! data.init ) && this.props.track ) {
			return 'track';
		}

		return 'init';
	}

	isLoading() {
		return this.props.isLoading && ! this.props.data[ this.getKey() ];
	}

	nextTask = () => {
		this.props.dispatch( incrementProgress() );
	}

	getComponent( task ) {
		if ( ! task ) {
			return null;
		}

		switch ( task.type ) {
			case 'chat':
				return Chat;

			case 'inboxes':
				return Inboxes;

			case 'questions':
				return Questions;

			default:
				return null;
		}
	}

	preloadNextSection() {
		const nextTask = this.getTask( 1, this.props.track ? 'track' : 'init' );
		const Screen = this.getComponent( nextTask );

		if ( Screen ) {
			Screen.preload();
		}
	}

	save() {
		hasSaved = true;
		let gender = 'X';
		if ( this.props.gender === 'male' ) {
			gender = 'M';
		} else if ( this.props.gender === 'female' ) {
			gender = 'F';
		}

		const data = {
			game_id: this.props.pin,
			gender: gender,
			track: this.props.track,
			answers: this.props.answers,
		};

		fetch( `${ api }save`, {
			method: 'POST',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
			},
			body: JSON.stringify( data ),
		} )
			.then( response => response.json() )
			.then( returnData => {
				this.setState( {
					done: true,
				}, () => {
					if ( returnData === 'Game saved' ) {
						this.props.reset();
					}
				} );
			} );
	}

	renderRoot() {
		this.preloadNextSection();

		if ( this.isLoading() ) {
			return <LoadingScreen pastDelay={ true } />;
		}

		return (
			<div>
				<TransitionGroup>
					{ this.renderRoute() }
				</TransitionGroup>
				{ this.renderIntroVideo() }
				{ this.renderVideo() }
			</div>
		);
	}

	renderRoute() {
		const task = this.getTask();
		const { done } = this.state;

		if ( ! task || done ) {
			if ( canUseDOM && ! hasSaved ) {
				this.save();
			}

			return (
				<Transition key="credits" timeout={ { enter: 500, exit: 0 } }>
					{ status => <Root><Credits status={ status } /></Root> }
				</Transition>
			);
		}

		// If user hasn't entered a pin. Return pin screen.
		if ( ! this.props.pin ) {
			Gender.preload();

			let background = null;
			let texts = [];

			if ( this.props.data && this.props.data.intro ) {
				background = this.props.data.intro.fields.img_url;
				texts = this.props.data.intro.fields.intro_text;
			}

			return (
				<Transition key="pin" timeout={ 0 }>
					<Root notFixedOnMob={ true }><Pin dispatch={ this.props.dispatch } background={ background } texts={ texts } /></Root>
				</Transition>
			);
		}

		// If user hasn't entered a gender. Return gender screen for student modules.
		if ( ! this.props.gender && window.brukHueConfig.category === 'students' ) {
			ConfirmSound.preload();

			return (
				<Transition key="gender" timeout={ { enter: 500, exit: 0 } }>
					{ status => <Root><Gender status={ status } dispatch={ this.props.dispatch } /></Root> }
				</Transition>
			);
		}

		// Return sound screen for student modules.
		if ( ! this.props.confirmedSound && window.brukHueConfig.category === 'students' ) {
			return (
				<Transition key="confirm-sound" timeout={ { enter: 500, exit: 0 } }>
					{ status => <Root><ConfirmSound status={ status } dispatch={ this.props.dispatch } /></Root> }
				</Transition>
			);
		}

		const Screen = this.getComponent( task );

		if ( ! Screen ) {
			return null;
		}

		let freeeScroll = false;

		if ( 'questions' === task.type ) {
			freeeScroll = true;
		}

		return (
			<Transition key="task" timeout={ 0 }>
				<Root innerRef={ this.root } freeScroll={ freeeScroll }>
					<Screen
						{ ...task.fields }
						nextTask={ this.nextTask }
						dispatch={ this.props.dispatch }
						root={ this.root }
						setEndVideo={ this.setEndVideo }
					/>
				</Root>
			</Transition>
		);
	}

	renderIntroVideo() {
		if ( this.props.pin && this.props.gender && this.props.confirmedSound ) {
			return null;
		}

		let background = null,
			video = null;

		if ( this.props.data && this.props.data.intro ) {
			background = this.props.data.intro.fields.img_url;
			video = this.props.data.intro.fields.video_url;
		}

		return (
			<IntroVideo
				background={ background }
				url={ video }
			/>
		);
	}

	renderVideo() {
		let task = null, i = 0;

		if ( this.state.endVideo ) {
			task = (
				<Video url={ this.state.endVideo } isMainWindow={ true } nextTask={ this.state.endVideoDone } />
			);
		} else {
			const x = ( this.props.pin && this.props.gender && this.props.confirmedSound ) ? 1 : 0;

			while ( i <= x ) {
				const nextTask = this.getTask( i, this.props.track ? 'track' : 'init' );

				if ( nextTask && nextTask.type === 'video' ) {
					task = (
						<Video
							{ ...nextTask.fields }
							isMainWindow={ this.props.pin && this.props.gender && i === 0 }
							nextTask={ this.nextTask }
						/>
					);
					break;
				}

				i++;
			}
		}

		return task;
	}

	render() {
		return (
			<div>
				<ThemeProvider theme={ this.props.theme }>
					{ this.renderRoot() }
				</ThemeProvider>
			</div>
		);
	}
}

function mapStateToProps( state ) : Object {
	const urlData = getUrlFromState( state );

	return {
		...state.user,
		url: urlData.url,
		urlKey: urlData.key,
		theme: state.theme,
	};
}

export default connect( mapStateToProps )( withApiData( props => props.url, props => props.urlKey, props => props.cachedData )( Routes ) );



// WEBPACK FOOTER //
// ./src/routes.js